import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CATEGORIE_GARA } from "../lib/helpers";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Impostazioni() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [soglie, setSoglie] = useState({ s30: 30, s15: 15, s7: 7, s1: 1 });
  const [filtri, setFiltri] = useState({ tipologie: [], regioni: "", importo_max: "", parole_escluse: "" });
  const [telematUser, setTelematUser] = useState("");
  const [telematPass, setTelematPass] = useState("");
  const [telematConfigured, setTelematConfigured] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [nuovo, setNuovo] = useState({ name: "", email: "", password: "", role: "operatore", dipendente_id: "" });

  const load = useCallback(async () => {
    try {
      const [s, u, d] = await Promise.all([api.get("/settings"), api.get("/users"), api.get("/dipendenti")]);
      setSettings(s.data);
      setUsers(u.data);
      setDipendenti(d.data);
      const sg = s.data.soglie || [30, 15, 7, 1];
      setSoglie({ s30: sg[0] ?? 30, s15: sg[1] ?? 15, s7: sg[2] ?? 7, s1: sg[3] ?? 1 });
      setEmailAlerts(s.data.email_alerts !== false);
      const f = s.data.import_filters || {};
      setFiltri({
        tipologie: f.tipologie || [],
        regioni: (f.regioni || []).join(", "),
        importo_max: f.importo_max ?? "",
        parole_escluse: (f.parole_escluse || []).join(", "),
      });
      setTelematUser(s.data.telemat_username || "");
      setTelematPass("");
      setTelematConfigured(!!s.data.telemat_configurato);
      setLastSync(s.data.telemat_last_sync || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvaSoglie = async () => {
    try {
      const arr = [soglie.s30, soglie.s15, soglie.s7, soglie.s1].map(Number).filter((n) => n > 0);
      const import_filters = {
        tipologie: filtri.tipologie,
        regioni: filtri.regioni.split(",").map((x) => x.trim()).filter(Boolean),
        importo_max: filtri.importo_max === "" ? null : Number(filtri.importo_max),
        parole_escluse: filtri.parole_escluse.split(",").map((x) => x.trim()).filter(Boolean),
      };
      await api.put("/settings", { soglie: arr, email_alerts: emailAlerts, import_filters, telemat_username: telematUser, telemat_password: telematPass || undefined });
      toast.success("Impostazioni salvate");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const syncOra = async () => {
    setSyncLoading(true);
    try {
      const { data } = await api.post("/telemat/sync");
      if (data.errore) toast.error(data.errore);
      else toast.success(`Sync Telemat: ${data.create} nuove gare · ${data.duplicate} già presenti · ${data.filtrate} filtrate`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleTipologia = (k) =>
    setFiltri((f) => ({
      ...f,
      tipologie: f.tipologie.includes(k) ? f.tipologie.filter((x) => x !== k) : [...f.tipologie, k],
    }));

  const inviaTest = async () => {
    setSendingTest(true);
    try {
      await api.post("/notifiche/test-email");
      toast.success("Email di test inviata al tuo indirizzo");
    } catch (e) {
      toast.error(formatApiError(e, "Invio fallito"));
    } finally {
      setSendingTest(false);
    }
  };

  const creaUtente = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...nuovo, dipendente_id: nuovo.dipendente_id || null });
      toast.success("Utente creato");
      setNuovo({ name: "", email: "", password: "", role: "operatore", dipendente_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const eliminaUtente = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  if (!settings) return <div className="py-16 text-center text-slate-500 text-sm" data-testid="impostazioni-loading">Caricamento impostazioni…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="impostazioni-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1">Soglie di allerta, notifiche email e gestione utenti.</p>
      </div>

      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="settings-soglie">
        <h2 className="text-base font-semibold text-slate-800 mb-1">Soglie di allerta scadenze</h2>
        <p className="text-xs text-slate-500 mb-4">Giorni prima della scadenza per evidenziazione visiva e digest email giornaliero (invio ore 7:30).</p>
        <div className="grid grid-cols-4 gap-4">
          {[["s30", "Attenzione (giallo)"], ["s15", "Urgente (arancio)"], ["s7", "Critico (rosso)"], ["s1", "Preavviso finale"]].map(([k, label]) => (
            <div key={k}>
              <label className={labelCls}>{label}</label>
              <input type="number" min="0" max="90" data-testid={`soglia-${k}`} value={soglie[k]}
                onChange={(e) => setSoglie((s) => ({ ...s, [k]: e.target.value }))} className={`${inputCls} font-code`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <Switch checked={emailAlerts} onCheckedChange={setEmailAlerts} data-testid="email-alerts-toggle" />
            <div>
              <div className="text-sm font-medium text-slate-800">Notifiche email automatiche</div>
              <div className="text-xs text-slate-500">Digest giornaliero delle scadenze agli amministratori</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={inviaTest} disabled={sendingTest} data-testid="test-email-button"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              <MailCheck className="h-4 w-4" /> {sendingTest ? "Invio…" : "Invia email di test"}
            </button>
            <button onClick={salvaSoglie} data-testid="settings-save-button"
              className="px-4 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
              Salva impostazioni
            </button>
          </div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="settings-import-filters">
        <h2 className="text-base font-semibold text-slate-800 mb-1">Importazione gare (MyTelemat / Telemat)</h2>
        <p className="text-xs text-slate-500 mb-4">
          Filtri applicati durante l'importazione da file CSV/XLS esportato da MyTelemat (pulsante "Importa MyTelemat" nella sezione Gare).
          Lascia vuoto per importare tutto. La deduplica su CIG/oggetto è sempre attiva.
        </p>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Solo queste tipologie (vuoto = tutte)</label>
            <div className="flex flex-wrap gap-3">
              {Object.entries(CATEGORIE_GARA).map(([k, v]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid={`filtro-tipologia-${k}`} checked={filtri.tipologie.includes(k)} onChange={() => toggleTipologia(k)} className="h-3.5 w-3.5 accent-blue-900" />
                  {v}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Solo queste regioni (vuoto = tutte)</label>
              <input data-testid="filtro-regioni" value={filtri.regioni} onChange={(e) => setFiltri((f) => ({ ...f, regioni: e.target.value }))}
                placeholder="es. Lombardia, Piemonte" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Importo massimo base d'asta (€)</label>
              <input type="number" min="0" data-testid="filtro-importo-max" value={filtri.importo_max} onChange={(e) => setFiltri((f) => ({ ...f, importo_max: e.target.value }))}
                placeholder="Nessun limite" className={`${inputCls} font-code`} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Escludi oggetti contenenti (parole separate da virgola)</label>
            <input data-testid="filtro-parole-escluse" value={filtri.parole_escluse} onChange={(e) => setFiltri((f) => ({ ...f, parole_escluse: e.target.value }))}
              placeholder="es. fornitura, manutenzione verde" className={inputCls} />
          </div>
          <p className="text-[11px] text-slate-400">
            I filtri si applicano sia all'import da file sia alla sincronizzazione automatica giornaliera.
          </p>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="settings-telemat">
        <h2 className="text-base font-semibold text-slate-800 mb-1">Collegamento MyTelemat (sincronizzazione automatica)</h2>
        <p className="text-xs text-slate-500 mb-4">
          Inserisci le credenziali dell'area riservata MyTelemat: ogni sera alle 19:30 StudioNex scarica la rassegna gare personalizzata
          e importa le novità come "Da valutare" (con filtri e deduplica). Le credenziali sono salvate sul server e non vengono mai mostrate.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nome utente MyTelemat</label>
            <input data-testid="telemat-username" value={telematUser} onChange={(e) => setTelematUser(e.target.value)} autoComplete="off" className={inputCls} placeholder="es. 670269" />
          </div>
          <div>
            <label className={labelCls}>Password MyTelemat {telematConfigured && <span className="text-green-700 normal-case">(salvata — lascia vuoto per mantenerla)</span>}</label>
            <input type="password" data-testid="telemat-password" value={telematPass} onChange={(e) => setTelematPass(e.target.value)} autoComplete="new-password" className={inputCls} placeholder={telematConfigured ? "••••••••" : "Password"} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100 flex-wrap gap-3">
          <div className="text-xs text-slate-500" data-testid="telemat-last-sync">
            {lastSync ? (
              <>Ultima sync: <span className="font-code">{new Date(lastSync.at).toLocaleString("it-IT")}</span> — {lastSync.errore
                ? <span className="text-red-700 font-semibold">errore: {lastSync.errore}</span>
                : <span>{lastSync.create} create · {lastSync.duplicate} già presenti · {lastSync.filtrate} filtrate</span>}</>
            ) : "Nessuna sincronizzazione eseguita."}
          </div>
          <button onClick={syncOra} disabled={syncLoading || !telematConfigured} data-testid="telemat-sync-button"
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950 disabled:opacity-60">
            {syncLoading ? "Sincronizzazione in corso…" : "Sincronizza ora"}
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="settings-users">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Utenti e ruoli</h2>
        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Nome</th>
              <th className="py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
              <th className="py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Ruolo</th>
              <th className="py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Dipendente collegato</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const dip = dipendenti.find((d) => d.id === u.dipendente_id);
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5 font-medium text-slate-800">{u.name}</td>
                  <td className="py-2.5 text-slate-600">{u.email}</td>
                  <td className="py-2.5">
                    <Badge variant="outline" className={u.role === "admin" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                      {u.role === "admin" ? "Amministratore" : "Operatore"}
                    </Badge>
                  </td>
                  <td className="py-2.5 text-slate-600 text-xs">{dip ? `${dip.nome} ${dip.cognome}` : "—"}</td>
                  <td className="py-2.5 text-right">
                    <button data-testid={`user-delete-${u.id}`} onClick={() => eliminaUtente(u)} className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Plus className="h-4 w-4" /> Nuovo utente</h3>
        <form onSubmit={creaUtente} className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Nome</label>
            <input required data-testid="user-form-name" value={nuovo.name} onChange={(e) => setNuovo((n) => ({ ...n, name: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input required type="email" data-testid="user-form-email" value={nuovo.email} onChange={(e) => setNuovo((n) => ({ ...n, email: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input required type="password" minLength={6} data-testid="user-form-password" value={nuovo.password} onChange={(e) => setNuovo((n) => ({ ...n, password: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Ruolo</label>
              <Select value={nuovo.role} onValueChange={(v) => setNuovo((n) => ({ ...n, role: v }))}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore">Operatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Dipendente</label>
              <Select value={nuovo.dipendente_id} onValueChange={(v) => setNuovo((n) => ({ ...n, dipendente_id: v === "__none" ? "" : v }))}>
                <SelectTrigger data-testid="user-form-dipendente"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="col-span-2 flex justify-end">
            <button type="submit" data-testid="user-form-submit" className="px-4 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
              Crea utente
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
